import { MONGO_OBJECTID_REGEX, SEMVER_REGEX, SLUG_REGEX } from '@readme/iso';
import { StructuredRegEx } from 'structured-regex';

/**
 * @example stable
 * @example 1.1
 * @example 1.1.1
 * @example 4-debug.test
 */
export const VERSION_REGEX = new StructuredRegEx<{ version: string }>(
  new RegExp(`stable|${SEMVER_REGEX.source}`, 'i'),
  {
    version: 0,
  },
);

/**
 * @example stable
 * @example 1.1
 * @example v1.0_most-normal-branch-name
 * @example v1.2.3_branch/test-branch_name
 * @example 123.456.789_im-a-branch/yipeeeee_its_branch_time
 * @example v123.456
 *
 * This regex doesn't enforce the git validity of a branch name due to the aggressive regex matching
 * needed for that check being detrimental to the URI regex construction. This instead checks:
 * 1) We have a valid semver, optionally with a `v`
 * 2) We optionally have a `_` seperator followed by any character combination
 *
 */
export const BRANCH_REGEX = new StructuredRegEx<{ branch: string }>(
  new RegExp(`(v{0,1})(${VERSION_REGEX.source})(_(.*))?`, 'i'),
  {
    branch: 0,
  },
);

export const CATEGORY_SLUG_REGEX = /(.*)/;
export const RESOURCE_ID_OR_SLUG = new RegExp(`${MONGO_OBJECTID_REGEX.source}|${SLUG_REGEX.source}`, 'i');

/**
 * @example `/changelogs/2023-06-15-update`
 * @example `/changelogs/e175cac316a79afdd0ad3afb`
 */
export const CHANGELOG_URI_REGEX = new StructuredRegEx<{ slug: string }>(
  new RegExp(`/changelogs/(${MONGO_OBJECTID_REGEX.source}|${SLUG_REGEX.source})`, 'i'),
  {
    slug: 1,
  },
);

/**
 * @example `/branches/v2_branch-name`
 * @example `/branches/v3.0-debug_branch/has-slash`
 */
export const BRANCH_URI_REGEX = new StructuredRegEx<{ branch: string }>(
  new RegExp(`/branches/(${BRANCH_REGEX.source})`, 'i'),
  {
    branch: 1,
  },
);

/**
 * @example `/branches/v2_branch-name/export/pdf-9cbb2f70`
 * @example `/branches/v3.0-debug_branch/has-slash/export/pdf-9cbb2f70`
 */
export const BRANCH_EXPORT_URI_REGEX = new StructuredRegEx<{ branch: string; uuid: string }>(
  new RegExp(`/(versions|branches)/(${BRANCH_REGEX.source})/export/(${SLUG_REGEX.source})`, 'i'),
  {
    branch: 2,
    uuid: 11,
  },
);

/**
 * @example `/versions/stable/custom_blocks/e175cac316a79afdd0ad3afb`
 * @example `/versions/45cbc4a0e4123f6920000000/custom_blocks/e175cac316a79afdd0ad3afb`
 */
export const CUSTOM_BLOCK_URI_REGEX = new StructuredRegEx<{ id: string; version: string }>(
  new RegExp(`/versions/(stable|${MONGO_OBJECTID_REGEX.source})/custom_blocks/(${MONGO_OBJECTID_REGEX.source})`, 'i'),
  { version: 1, id: 2 },
);

/**
 * @example `/branches/stable/custom_blocks/page-slug`
 * @example `/branches/1.0.0/custom_blocks/page-slug`
 *
 *  TODO: remove `| versions` once the /versions routes are fully sunsetted
 */
export const CUSTOM_BLOCK_GIT_URI_REGEX = new StructuredRegEx<{ tag: string; branch: string }>(
  new RegExp(`/(versions|branches)/(${BRANCH_REGEX.source})/custom_blocks/(\\w+)`, 'i'),
  {
    branch: 2,
    tag: 11,
  },
);

/**
 * @example `/branches/stable/custom_pages/our-custom-page`
 * @example `/branches/1.0.0/custom_pages/e175cac316a79afdd0ad3afb`
 *
 * TODO: remove `| versions` once the /versions routes are fully sunsetted
 */
export const CUSTOM_PAGE_URI_REGEX = new StructuredRegEx<{ id: string; branch: string }>(
  new RegExp(`/(versions|branches)/(${BRANCH_REGEX.source})/custom_pages/(${RESOURCE_ID_OR_SLUG.source})`, 'i'),
  { branch: 2, id: 11 },
);

/**
 * @example `/branches/stable/guides/getting-started`
 * @example `/branches/1.1/guides/getting-started`
 * @example `/branches/stable/reference/create-pet`
 * @example `/branches/1.1/reference/create-pet`
 *
 * TODO: remove `| versions` once the /versions routes are fully sunsetted
 */
export const PAGE_URI_REGEX = new StructuredRegEx<{ section: 'guides' | 'reference'; slug: string; branch: string }>(
  new RegExp(`/(versions|branches)/(${BRANCH_REGEX.source})/(guides|reference)/(${SLUG_REGEX.source})`, 'i'),
  {
    branch: 2,
    section: 11,
    slug: 12,
  },
);

/**
 * @example `/branches/stable/categories/guides/documentation`
 * @example `/branches/1.1/categories/guides/documentation`
 *
 * TODO: remove `| versions` once the /versions routes are fully sunsetted
 */
export const CATEGORY_URI_REGEX = new StructuredRegEx<{
  section: 'guides' | 'reference';
  slug: string;
  branch: string;
}>(
  new RegExp(
    `/(versions|branches)/(${BRANCH_REGEX.source})/categories/(guides|reference)/(${CATEGORY_SLUG_REGEX.source})`,
    'i',
  ),
  { branch: 2, section: 11, slug: 12 },
);

/**
 * @example `/images/e175cac316a79afdd0ad3afb`
 */
export const IMAGE_URI_REGEX = new StructuredRegEx<{ identifier: string }>(
  new RegExp(`/images/(${MONGO_OBJECTID_REGEX.source})`, 'i'),
  { identifier: 1 },
);

/**
 * @example `/branches/stable/recipes/make-an-api-call`
 * @example `/branches/1.1/recipes/make-an-api-call`
 *
 * TODO: remove `| versions` once the /versions routes are fully sunsetted
 */
export const RECIPE_URI_REGEX = new StructuredRegEx<{ slug: string; branch: string }>(
  new RegExp(`/(versions|branches)/(${BRANCH_REGEX.source})/recipes/(${SLUG_REGEX.source})`, 'i'),
  { branch: 2, slug: 11 },
);

/**
 * @example petstore
 * @example devilbert-clone
 */
export const PROJECT_SUBDOMAIN_REGEX = new StructuredRegEx<{ subdomain: string }>(/[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*/, {
  subdomain: 0,
});

/**
 * @example me
 * @example petstore
 * @example devilbert-clone
 */
export const PROJECT_SUBDOMAIN_PATH_REGEX = new StructuredRegEx<{ subdomain: string }>(
  new RegExp(`(me|${PROJECT_SUBDOMAIN_REGEX.source})`, 'i'),
  { subdomain: 1 },
);

/**
 * @example `/projects/me`
 * @example `/projects/petstore`
 * @example `/projects/devilbert-clone`
 */
export const PROJECT_URI_REGEX = new StructuredRegEx<{ subdomain: string }>(
  new RegExp(`/projects/${PROJECT_SUBDOMAIN_PATH_REGEX.source}`, 'i'),
  { subdomain: 1 },
);

/**
 * @example `petstore.json`
 * @example `petstore.yaml`
 * @example `petstore.yml`
 */
export const API_FILENAME_REGEX = new StructuredRegEx<{ filename: string }>(
  new RegExp(`(${SLUG_REGEX.source}.(json|yaml|yml))`, 'i'),
  { filename: 1 },
);

/**
 * @example `/branches/stable/apis/petstore.json`
 * @example `/branches/1.1/apis/petstore.json`
 * @example `/branches/stable/apis/petstore.yaml`
 * @example `/branches/1.1/apis/petstore.yaml`
 *
 * TODO: remove `| versions` once the /versions routes are fully sunsetted
 */
export const API_URI_REGEX = new StructuredRegEx<{ filename: string; branch: string }>(
  new RegExp(`/(versions|branches)/(${BRANCH_REGEX.source})/apis/(${API_FILENAME_REGEX.source})`, 'i'),
  {
    branch: 2,
    filename: 12,
  },
);

/**
 * @example `/branches/stable/ai/audits/68ae4679846be1d06fa7fb12`
 * @example `/branches/1.1/ai/audits/68ae4679846be1d06fa7fb12`
 *
 * TODO: remove `| versions` once the /versions routes are fully sunsetted
 */
export const DOC_AUDIT_REPORT_URI_REGEX = new StructuredRegEx<{ branch: string; id: string }>(
  new RegExp(`/(versions|branches)/(${BRANCH_REGEX.source})/ai/audits/(${MONGO_OBJECTID_REGEX.source})`, 'i'),
  {
    branch: 2,
    id: 11,
  },
);

/**
 * @example `/branches/stable/review/reports?report_id=68ae4679846be1d06fa7fb12`
 * @example `/branches/1.1/review/reports?report_id=68ae4679846be1d06fa7fb12`
 *
 * TODO: remove `| versions` once the /versions routes are fully sunsetted
 */
export const BRANCH_REVIEW_REPORT_URI_REGEX = new StructuredRegEx<{ branch: string; id: string }>(
  new RegExp(
    `/(versions|branches)/(${BRANCH_REGEX.source})/review/reports\\?report_id=(${MONGO_OBJECTID_REGEX.source})`,
    'i',
  ),
  {
    branch: 2,
    id: 11,
  },
);
